'use client';

import {GetSearchProductsFacetsResponse} from '@winelivery-org/wl-client-sdk-js/dist/models/search';
import {useTranslations} from 'next-intl';
import {ChangeEvent, useMemo, useState} from 'react';
import {Checkbox} from '../../../../../components/design-system';
import {useFindFacetsQuery} from '../query/useFindFacetsQuery';
import FilterSection from './FilterSection';
import useFilters from './hooks/useFilters';

interface Props {
  facetKey: keyof Omit<GetSearchProductsFacetsResponse, 'tags' | 'prices'>;
  filterKey: Keys;
  filterChildKey?: Keys;
}

type Keys = 'country' | 'region' | 'characteristic' | 'occasion';

function FilterSectionFacet({facetKey, filterKey, filterChildKey}: Props) {
  const t = useTranslations('shop');

  const {filters, set, del} = useFilters();

  const {data: facets, isLoading} = useFindFacetsQuery();

  const [expanded, setExpanded] = useState(false);

  const items = useMemo(() => {
    if (!facets) {
      return [];
    }
    const facet = facets[facetKey];
    if (expanded) {
      return facet;
    }

    return facet.reduce((acc, curr) => {
      // @ts-ignore
      const count = acc.reduce((a, c) => a + c.children.length + 1, 0);
      if (count > 9) {
        return acc;
      }
      return [
        ...acc,
        {
          ...curr,
          children: curr.children.slice(0, 10 - count - 1),
        },
      ];
    }, []);
  }, [expanded, facets]);

  const totalItems = useMemo(() => {
    if (!facets) {
      return 0;
    }
    const facet = facets[facetKey];
    return facet.reduce((acc, curr) => {
      return acc + 1 + curr.children.length;
    }, 0);
  }, [facets]);

  function handleChange(key: Keys, event: ChangeEvent<HTMLInputElement>) {
    let current = filters[key];

    if (!current || current.length === 0) {
      return set({[key]: [event.target.value]});
    }

    current = current.includes(event.target.value)
      ? current.filter((filter) => filter !== event.target.value)
      : current.concat(event.target.value);

    current.length === 0 ? del(key) : set({[key]: current});
  }

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <FilterSection title={t(`filter.${filterKey}.title`)}>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="space-y-2">
            <Checkbox
              name={facetKey}
              onChange={(value) => handleChange(filterKey, value)}
              checked={((filters[filterKey] as string[]) || []).includes(
                item.slug,
              )}
              value={item.slug}
              label={item.name}
            />
            {item.children.map((child, childIndex) => (
              <div
                key={`${index}-${childIndex}`}
                className="pl-6 space-y-2"
              >
                <Checkbox
                  name={facetKey}
                  onChange={(value) =>
                    handleChange(filterChildKey || filterKey, value)
                  }
                  checked={(
                    (filters[filterChildKey || filterKey] as string[]) || []
                  ).includes(child.slug || item.slug)}
                  value={child.slug}
                  label={child.name}
                />
              </div>
            ))}
          </div>
        ))}
        {totalItems > 10 && (
          <div
            onClick={() => setExpanded((prev) => !prev)}
            className="text-greyscale-body underline text-base font-medium tracking-tight cursor-pointer"
          >
            {expanded ? t('filter.less') : t('filter.more')}
          </div>
        )}
      </div>
    </FilterSection>
  );
}

export default FilterSectionFacet;
